import React, { useRef } from "react";
import { Box, Stack, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { Close, Download, Save } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import { saveAs } from "file-saver";
import * as quillToWord from "quill-to-word";
import ActionButton, { DarkGreyButton } from "../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import Confirmation from "../../../components/commonComponents/Confirmation";
import {
  StyledBlackInformationTypography,
  StyledDarkRedInformationTypography,
} from "../../../components/commonComponents/StyledComponents/styled";
import { saveCampaignInternalNote } from "../../../api_calls/campaignInternalNotes";

// Cyreen-internal notes for a campaign. Editor and export follow the store Internal Notes tab.
const CampaignInternalNotes = ({ open, size, closeModal, campaignRecord }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const [value, setValue] = React.useState(
    typeof currentStateData.campaign_internal_note !== "undefined" ? currentStateData.campaign_internal_note : ""
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const quillRef = useRef(null);

  const handleExportDocx = async () => {
    const delta = quillRef.current.getEditor().getContents();
    const quillToWordConfig = {
      exportAs: "blob",
    };
    const docAsBlob = await quillToWord.generateWord(delta, quillToWordConfig);
    saveAs(docAsBlob, "campaignInternalNote_" + campaignRecord.campaign_id + ".docx");
  };

  const saveNoteText = () => {
    // notes can be empty: an editor without text is saved as "" instead of Quill's "<p><br></p>"
    const editorText = quillRef.current.getEditor().getText();
    const noteText = editorText.trim() === "" ? "" : value;
    const noteId =
      typeof currentStateData.campaign_internal_note_id !== "undefined" ? currentStateData.campaign_internal_note_id : "";
    saveCampaignInternalNote(noteText, campaignRecord.campaign_id, noteId).then((info) => {
      if (info.success === true) {
        closeModal();
      }
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => {
          e.preventDefault();
        }}
        fullWidth={true}
        maxWidth={size}
      >
        <DialogTitle>
          <strong>Internal Campaign Notes</strong>
        </DialogTitle>
        <DialogContent>
          <Stack direction={"row"} spacing={3}>
            <Box>
              <Stack direction={"row"} spacing={1}>
                <StyledDarkRedInformationTypography>Campaign Name :</StyledDarkRedInformationTypography>
                <StyledBlackInformationTypography>{campaignRecord.campaign_name}</StyledBlackInformationTypography>
              </Stack>
            </Box>
            <Box>
              <Stack direction={"row"} spacing={1}>
                <StyledDarkRedInformationTypography>Campaign ID :</StyledDarkRedInformationTypography>
                <StyledBlackInformationTypography>{campaignRecord.campaign_id}</StyledBlackInformationTypography>
              </Stack>
            </Box>
          </Stack>
          <Box height={20} />
          <Box sx={{ "& .ql-editor": { minHeight: 250 } }}>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={value}
              onChange={(content, delta, source) => {
                setValue(content);
                // Quill also fires onChange when it normalises the loaded HTML; only typing counts as a change
                if (source === "user") {
                  setHasUnsavedChanges(true);
                }
              }}
              modules={{
                toolbar: [
                  [{ header: [1, 2, false] }],
                  ["bold", "italic", "underline", "strike", "blockquote"],
                  [{ list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
                  ["clean"],
                ],
                // matchVisual adds an empty paragraph before headers/lists/quotes every time saved HTML is loaded
                clipboard: { matchVisual: false },
              }}
            />
          </Box>
        </DialogContent>

        <Box height={20} />
        <DialogActions>
          <Stack direction={"row"} spacing={2}>
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                if (hasUnsavedChanges === true) {
                  setShowConfirmation(true);
                } else {
                  closeModal();
                }
              }}
              label="Cancel"
              icon={<Close />}
            />

            <DarkGreyButton
              onClick={(e) => {
                e.preventDefault();
                handleExportDocx();
              }}
              label="Download"
              icon={<Download />}
            />

            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                saveNoteText();
              }}
              label="Save"
              icon={<Save />}
            />
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ------------ discard unsaved changes confirmation ------------ */}
      {showConfirmation === true && (
        <Confirmation
          confirmationActionCall={() => {
            closeModal();
          }}
          showConfirmationPopup={showConfirmation}
          confirmationTitle={"Discard Changes?"}
          confirmationMessage={
            <>
              <Typography variant="body2">You have unsaved changes to the internal notes. These will be discarded.</Typography>
              <Box height={10} />
              <Typography variant="body2">Do you want to proceed?</Typography>
            </>
          }
          closePopup={() => {
            setShowConfirmation(false);
          }}
        />
      )}
    </>
  );
};

export default CampaignInternalNotes;
